import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, ChevronDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import BicycleFloorFormSlider from '../components/BicycleFloorFormSlider';
import BicycleAddonFormSlider from '../components/BicycleAddonFormSlider';
import BicycleTypeFormSlider from '../components/BicycleTypeFormSlider';
import BicycleFloorDetailsSlider from '../components/BicycleFloorDetailsSlider';
import { bicycleFloorService, bicycleAddonService, bicycleTypeService } from '../services/bicycleService';
import { BicycleFloor, BicycleAddon, BicycleType } from '../types/bicycle';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Bicycles: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [bicycleFloors, setBicycleFloors] = useState<BicycleFloor[]>([]);
  const [bicycleAddons, setBicycleAddons] = useState<BicycleAddon[]>([]);
  const [bicycleTypes, setBicycleTypes] = useState<BicycleType[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedProject, setSelectedProject] = useState('');

  // Delete modals state
  const [showDeleteFloorModal, setShowDeleteFloorModal] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<BicycleFloor | null>(null);
  const [showDeleteAddonModal, setShowDeleteAddonModal] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState<BicycleAddon | null>(null);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<BicycleType | null>(null);

  // Floor details state
  const [selectedBicycleFloorId, setSelectedBicycleFloorId] = useState<string | null>(null);

  // Get drawer state from URL params
  const isFloorDrawerOpen = searchParams.get('action') === 'new-floor' || (!!searchParams.get('edit') && searchParams.get('type') === 'floor');
  const editFloorId = searchParams.get('type') === 'floor' ? searchParams.get('edit') : null;

  const isAddonDrawerOpen = searchParams.get('action') === 'new-addon' || (!!searchParams.get('edit') && searchParams.get('type') === 'addon');
  const editAddonId = searchParams.get('type') === 'addon' ? searchParams.get('edit') : null;

  const isTypeDrawerOpen = searchParams.get('action') === 'new-type' || (!!searchParams.get('edit') && searchParams.get('type') === 'type');
  const editTypeId = searchParams.get('type') === 'type' ? searchParams.get('edit') : null;

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProject]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);

      // Automatically select the first project if no project is selected
      if (response.data.projects.length > 0 && !selectedProject) {
        setSelectedProject(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchAllData = async () => {
    if (!workspaceId || !selectedProject) return;

    try {
      setLoading(true);
      setError('');

      const params: any = {
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
        projectId: selectedProject
      };

      const [floorsResponse, addonsResponse, typesResponse] = await Promise.all([
        bicycleFloorService.getAll(workspaceId, params),
        bicycleAddonService.getAll(workspaceId, params),
        bicycleTypeService.getAll(workspaceId, params)
      ]);

      setBicycleFloors(floorsResponse.bicycleFloors);
      setBicycleAddons(addonsResponse.bicycleAddons);
      setBicycleTypes(typesResponse.bicycleTypes);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bicycle data');
      console.error('Error fetching bicycle data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAddFloor = () => {
    setSearchParams({ action: 'new-floor' });
  };

  const handleAddAddon = () => {
    setSearchParams({ action: 'new-addon' });
  };

  const handleAddType = () => {
    setSearchParams({ action: 'new-type' });
  };

  const handleEditFloor = (id: string) => {
    setSearchParams({ edit: id, type: 'floor' });
  };

  const handleEditAddon = (id: string) => {
    setSearchParams({ edit: id, type: 'addon' });
  };

  const handleEditType = (id: string) => {
    setSearchParams({ edit: id, type: 'type' });
  };

  // Cancel handlers - only close drawer, no refetch
  const handleCloseFloorDrawer = () => {
    setSearchParams({});
  };

  const handleCloseAddonDrawer = () => {
    setSearchParams({});
  };

  const handleCloseTypeDrawer = () => {
    setSearchParams({});
  };

  // Success handlers - close drawer AND refetch data
  const handleFloorSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleAddonSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleTypeSuccess = () => {
    setSearchParams({});
    fetchAllData();
  };

  const handleDeleteFloorClick = (floor: BicycleFloor) => {
    setFloorToDelete(floor);
    setShowDeleteFloorModal(true);
  };

  const handleDeleteFloorConfirm = async () => {
    if (!floorToDelete || !workspaceId) return;

    try {
      await bicycleFloorService.delete(workspaceId, floorToDelete._id);
      setShowDeleteFloorModal(false);
      setFloorToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bicycle floor');
      console.error('Error deleting bicycle floor:', err);
    }
  };

  const handleDeleteAddonClick = (addon: BicycleAddon) => {
    setAddonToDelete(addon);
    setShowDeleteAddonModal(true);
  };

  const handleDeleteAddonConfirm = async () => {
    if (!addonToDelete || !workspaceId) return;

    try {
      await bicycleAddonService.delete(workspaceId, addonToDelete._id);
      setShowDeleteAddonModal(false);
      setAddonToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bicycle add-on');
      console.error('Error deleting bicycle add-on:', err);
    }
  };

  const handleDeleteTypeClick = (type: BicycleType) => {
    setTypeToDelete(type);
    setShowDeleteTypeModal(true);
  };

  const handleDeleteTypeConfirm = async () => {
    if (!typeToDelete || !workspaceId) return;

    try {
      await bicycleTypeService.delete(workspaceId, typeToDelete._id);
      setShowDeleteTypeModal(false);
      setTypeToDelete(null);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bicycle type');
      console.error('Error deleting bicycle type:', err);
    }
  };

  const handleFloorRowClick = (floorId: string) => {
    setSelectedBicycleFloorId(floorId);
  };

  const handleCloseBicycleFloorDetails = () => {
    setSelectedBicycleFloorId(null);
    fetchAllData();
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Bicycle</h2>
                <p className="builders-section-subtitle">
                  Manage bicycle floors, add-ons, and types
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  className="form-input"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ width: '250px' }}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Bicycle Floors Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Bicycle Floors</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddFloor}>
                  Add Floor
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bicycleFloors.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No bicycle floors found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      bicycleFloors.map((floor) => (
                        <tr
                          key={floor._id}
                          onClick={() => handleFloorRowClick(floor._id)}
                        >
                          <td><strong>{floor.projectName || '-'}</strong></td>
                          <td><strong>{floor.name}</strong></td>
                          <td>{floor.total}</td>
                          <td>{floor.available}</td>
                          <td>{floor.marketingHold}</td>
                          <td>{floor.salesHold}</td>
                          <td>{floor.sold}</td>
                          <td>{floor.customerSelection}</td>
                          <td>{floor.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFloor(floor._id);
                                }}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFloorClick(floor);
                                }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bicycle Add-ons Table */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Bicycle Add-ons</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddAddon}>
                  Add Add-on
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Capable</th>
                      <th>Sold</th>
                      <th>Cost</th>
                      <th>Price</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bicycleAddons.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No bicycle add-ons found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      bicycleAddons.map((addon) => (
                        <tr key={addon._id}>
                          <td><strong>{addon.projectName || '-'}</strong></td>
                          <td><strong>{addon.name}</strong></td>
                          <td>{addon.total}</td>
                          <td>{addon.available}</td>
                          <td>{addon.capable}</td>
                          <td>{addon.sold}</td>
                          <td>${addon.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>${addon.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditAddon(addon._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteAddonClick(addon)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bicycle Types Table */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>Bicycle Types</h3>
                <button className="btn btn-primary btn-sm" onClick={handleAddType}>
                  Add Type
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Name</th>
                      <th>Total</th>
                      <th>Available</th>
                      <th>Marketing Hold</th>
                      <th>Sales Hold</th>
                      <th>Sold</th>
                      <th>Customer Selection</th>
                      <th>Customer Submitted</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bicycleTypes.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                          No bicycle types found. Click "Add" to create one.
                        </td>
                      </tr>
                    ) : (
                      bicycleTypes.map((type) => (
                        <tr key={type._id}>
                          <td><strong>{type.projectName || '-'}</strong></td>
                          <td><strong>{type.name}</strong></td>
                          <td>{type.total}</td>
                          <td>{type.available}</td>
                          <td>{type.marketingHold}</td>
                          <td>{type.salesHold}</td>
                          <td>{type.sold}</td>
                          <td>{type.customerSelection}</td>
                          <td>{type.customerSubmitted}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditType(type._id)}
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-icon-danger"
                                onClick={() => handleDeleteTypeClick(type)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Bicycle Floor Create/Edit */}
        <Drawer
          isOpen={isFloorDrawerOpen}
          onClose={handleCloseFloorDrawer}
          title={editFloorId ? 'Edit Bicycle Floor' : 'Add Bicycle Floor'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseFloorDrawer} form="bicycle-floor-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="bicycle-floor-form">
                {editFloorId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isFloorDrawerOpen && (
            <BicycleFloorFormSlider
              workspaceId={workspaceId!}
              floorId={editFloorId || undefined}
              onSuccess={handleFloorSuccess}
              onCancel={handleCloseFloorDrawer}
            />
          )}
        </Drawer>

        {/* Drawer for Bicycle Add-on Create/Edit */}
        <Drawer
          isOpen={isAddonDrawerOpen}
          onClose={handleCloseAddonDrawer}
          title={editAddonId ? 'Edit Bicycle Add-on' : 'Add Bicycle Add-on'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseAddonDrawer} form="bicycle-addon-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="bicycle-addon-form">
                {editAddonId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isAddonDrawerOpen && (
            <BicycleAddonFormSlider
              workspaceId={workspaceId!}
              addonId={editAddonId || undefined}
              onSuccess={handleAddonSuccess}
              onCancel={handleCloseAddonDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal - Bicycle Floor */}
        {showDeleteFloorModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteFloorModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Bicycle Floor</h3>
              <p>Are you sure you want to delete "{floorToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteFloorModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteFloorConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Bicycle Add-on */}
        {showDeleteAddonModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteAddonModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Bicycle Add-on</h3>
              <p>Are you sure you want to delete "{addonToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteAddonModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteAddonConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Bicycle Type */}
        {showDeleteTypeModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteTypeModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Bicycle Type</h3>
              <p>Are you sure you want to delete "{typeToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteTypeModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteTypeConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drawer for Bicycle Type Create/Edit */}
        <Drawer
          isOpen={isTypeDrawerOpen}
          onClose={handleCloseTypeDrawer}
          title={editTypeId ? 'Edit Bicycle Type' : 'Add Bicycle Type'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseTypeDrawer} form="bicycle-type-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="bicycle-type-form">
                {editTypeId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isTypeDrawerOpen && (
            <BicycleTypeFormSlider
              workspaceId={workspaceId!}
              typeId={editTypeId || undefined}
              onSuccess={handleTypeSuccess}
              onCancel={handleCloseTypeDrawer}
            />
          )}
        </Drawer>

        {/* Bicycle Floor Details Slider */}
        {selectedBicycleFloorId && workspaceId && (
          <BicycleFloorDetailsSlider
            bicycleFloorId={selectedBicycleFloorId}
            workspaceId={workspaceId}
            onClose={handleCloseBicycleFloorDetails}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Bicycles;
